import React from "react";

const TermsOfService = () => {
  return (
    <div className="legal-section">
      <h2>Terms of Use</h2>
      <p>Last Updated: July 26th, 2025</p>

      <h3>Introduction</h3>
      <p>
        Welcome to SocratesLab ("we," "our," or "us"). These Terms of Use
        ("Terms") govern your access to and use of our website(s), including all
        related pages, subdomains, content, features, and services
        (collectively, the "Site"), provided by SocratesLab at
        https://www.socrateslab.ca (or such other URLs as we may designate).
      </p>

      <p>
        By accessing or using our Site, you acknowledge that you have read,
        understood, and agree to be bound by these Terms, as well as any other
        policies referenced herein, including our Privacy Policy. If you do not
        agree with these Terms, do not use the Site.
      </p>

      <h3>Note Regarding Organizational Access:</h3>
      <p>
        If you have been granted access to our learning management system or
        related AI-powered services by a school, employer, institution, or other
        organization ("Organization"), additional terms and conditions
        ("Organizational Terms") may apply. These Organizational Terms will
        govern your use of the platform on the Organization's behalf and, in the
        event of any conflict between these Terms and the Organizational Terms,
        the Organizational Terms shall control with respect to your use on
        behalf of the Organization. For questions about the Organizational
        Terms, please contact your Organization.
      </p>

      <h3>1. Modifications to Terms</h3>
      <p>
        We reserve the right to modify these Terms at any time, at our sole
        discretion. If we do so, we will update the "Last Updated" date above.
        Continued use of the Site after any such changes constitutes your
        acceptance of the modified Terms. If you do not agree to the modified
        Terms, you must discontinue using the Site.
      </p>

      <h3>2. Description of the Service</h3>
      <p>
        SocratesLab provides an online platform, tools, and educational content
        that may include artificial intelligence ("AI") features to enhance
        learning experiences (the "Service"). The Site and Service may include
        interactive modules, informational materials, user-generated content,
        and other educational resources. The purpose of the Site and Service is
        to provide university students, faculty, and other learners with
        educational tools and solutions.
      </p>
      <p>
        <strong>Context:</strong> Our platform is designed primarily for use in
        universities, companies, and higher education settings. Users are
        expected to be adults (18+) enrolled in or affiliated with educational
        institutions.
      </p>

      <h3>3. Permitted Use and Restrictions</h3>
      <p>
        (a) Non-Commercial Use: Unless otherwise agreed in writing, you may use
        the Site and its content (including text, images, videos, software, and
        other materials, collectively "Content") solely for your personal,
        non-commercial educational purposes or to learn about our products and
        services.
      </p>

      <p>(b) Prohibited Uses:</p>
      <p>
        • You may not reproduce, distribute, modify, create derivative works of,
        publicly display, publicly perform, republish, download, store, or
        transmit any of the Content except as expressly permitted by these
        Terms.
        <br />
        • You must not alter, remove, or obscure any copyright, trademark, or
        other proprietary notices from copies of the Content.
        <br />
        • You must not use the Site in any manner that could disable,
        overburden, damage, or impair it, or interfere with another party's use
        and enjoyment of the Site.
        <br />
        • You must not attempt to gain unauthorized access to any parts of the
        Site, user accounts (other than your own), or any computer systems or
        networks connected to the Site.
        <br />• You must not use the Site for any unlawful purpose or in
        violation of any applicable laws or regulations.
      </p>

      <h3>4. Intellectual Property Rights</h3>
      <p>(a) Ownership of Content:</p>
      <p>
        The Site and its Content are protected by Canadian and international
        copyright, trademark, and other intellectual property laws. All rights,
        title, and interest in and to the Site and Content are owned or
        controlled by SocratesLab or its licensors. Except for the limited
        rights expressly granted to you in these Terms, no other rights are
        granted, implied, or otherwise.
      </p>

      <p>(b) Trademarks:</p>
      <p>
        All trademarks, logos, service marks, and trade names displayed on the
        Site are the property of SocratesLab or third parties who have
        authorized their use. You are prohibited from using any such marks
        without prior written permission.
      </p>

      <h3>5. User-Generated Content and Feedback</h3>
      <p>(a) User-Generated Content:</p>
      <p>
        If you upload, post, or otherwise submit any content (including but not
        limited to text, data, photographs, images, audiovisual works, or other
        materials) to the Site ("User Content"), you represent and warrant that
        you have the right to do so and that your User Content complies with
        these Terms and all applicable laws. You retain any ownership rights you
        have in your User Content. However, by submitting User Content, you
        grant SocratesLab a worldwide, perpetual, irrevocable, non-exclusive,
        royalty-free, sublicensable, and transferable license to use, reproduce,
        distribute, create derivative works of, publicly display, publicly
        perform, and otherwise exploit your User Content in connection with the
        Site and our business, without any further notice or compensation to
        you. You acknowledge that User Content is non-confidential.
      </p>

      <p>(b) Feedback:</p>
      <p>
        If you provide any suggestions, comments, or feedback regarding the Site
        or Service ("Feedback"), we may use and incorporate such Feedback
        without any obligation to you, and you hereby assign all right, title,
        and interest in and to such Feedback to us.
      </p>

      <h3>6. Links to Third-Party Websites</h3>
      <p>
        The Site may contain links to third-party websites or resources
        ("Third-Party Sites") for your convenience. We do not endorse, control,
        or assume responsibility for any Third-Party Sites, their content,
        privacy policies, terms, or practices. Accessing any Third-Party Site is
        at your own risk.
      </p>

      <h3>7. Security and Account Responsibility</h3>
      <p>
        If you create an account with us, you are responsible for maintaining
        the confidentiality of your account credentials and for all activities
        that occur under your account. You agree to notify us immediately of any
        unauthorized use of your account or breach of security.
      </p>

      <h3>8. Privacy Policy</h3>
      <p>
        Your use of the Site and Service is also governed by our [Privacy
        Policy], which is incorporated by reference into these Terms. Please
        review our Privacy Policy to understand our data collection, use, and
        disclosure practices.
      </p>

      <h3>9. AI-Driven Features and Cross-Border Processing</h3>
      <p>
        <strong>AI Features for Plus Users:</strong> AI-powered features are
        available exclusively to users with "Plus" subscriptions and include AI
        tutoring, automated feedback, audio transcription, and content
        generation.
      </p>
      <p>
        <strong>Data Processing Location:</strong> When you use AI features,
        your educational content (text responses, audio recordings, and course
        materials) may be processed by OpenAI, located in the United States. By
        subscribing to Plus features, you explicitly consent to this
        cross-border data processing.
      </p>
      <p>
        <strong>AI Accuracy and Limitations:</strong> While we strive for
        accuracy, these AI-driven features are provided "as is." AI outcomes may
        vary and should not be relied upon as definitive or professional advice.
        All AI assessments are subject to instructor review and override.
      </p>
      <p>
        <strong>Opt-out Option:</strong> You can opt-out of all AI features by
        choosing or downgrading to a non-Plus account. All core educational
        functionality remains available without AI features.
      </p>

      <h3>10. Disclaimer of Warranties</h3>
      <p>
        YOUR USE OF THE SITE, THE SERVICE, AND ANY CONTENT IS AT YOUR SOLE RISK.
        THE SITE, SERVICE, AND CONTENT ARE PROVIDED ON AN "AS IS" AND "AS
        AVAILABLE" BASIS, WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS OR
        IMPLIED. TO THE FULLEST EXTENT PERMITTED BY LAW, WE DISCLAIM ALL
        WARRANTIES, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF
        MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE,
        NON-INFRINGEMENT, AND ACCURACY. WE DO NOT WARRANT THAT THE SITE OR
        SERVICE WILL BE UNINTERRUPTED, SECURE, ERROR-FREE, OR FREE FROM VIRUSES
        OR OTHER HARMFUL COMPONENTS, OR THAT ANY ERRORS WILL BE CORRECTED.
      </p>

      <h3>11. Limitation of Liability</h3>
      <p>
        TO THE FULLEST EXTENT PERMITTED BY LAW, IN NO EVENT WILL SOCRATESLAB,
        ITS AFFILIATES, LICENSORS, SERVICE PROVIDERS, EMPLOYEES, AGENTS, OR
        REPRESENTATIVES BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL,
        CONSEQUENTIAL, SPECIAL, EXEMPLARY, OR PUNITIVE DAMAGES (INCLUDING
        WITHOUT LIMITATION LOST PROFITS, LOST DATA, OR BUSINESS INTERRUPTION)
        ARISING OUT OF OR IN CONNECTION WITH YOUR USE OF THE SITE, SERVICE, OR
        CONTENT, EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH
        DAMAGES. IF APPLICABLE LAW DOES NOT ALLOW THE LIMITATION OR EXCLUSION OF
        LIABILITY, THE ABOVE LIMITATIONS WILL APPLY TO THE MAXIMUM EXTENT
        PERMITTED BY LAW.
      </p>

      <h3>12. Indemnification</h3>
      <p>
        You agree to indemnify, defend, and hold harmless SocratesLab, its
        affiliates, licensors, service providers, employees, agents, and
        representatives from and against all claims, losses, liabilities,
        expenses, damages, and costs (including reasonable attorneys' fees)
        arising from or related to (i) your use or misuse of the Site, Service,
        or Content, (ii) your User Content, (iii) your violation of these Terms,
        or (iv) your violation of any applicable law or regulation.
      </p>

      <h3>13. Dispute Resolution; No Class Actions</h3>
      <p>
        To the extent permitted by law, you agree that any dispute arising out
        of or relating to these Terms, the Site, or the Service shall be brought
        on an individual basis only, and not as a plaintiff or class member in
        any purported class, collective, or representative proceeding. You waive
        any right to a jury trial in connection with any such dispute.
      </p>

      <h3>14. Compliance with Laws</h3>
      <p>
        You agree to use the Site in compliance with all applicable laws and
        regulations. You are responsible for determining whether your use of the
        Site is legal in your jurisdiction.
      </p>

      <h3>15. Governing Law and Jurisdiction</h3>
      <p>
        These Terms and any dispute arising out of or in connection with them
        shall be governed by and construed in accordance with the laws of the
        Province of Quebec and the federal laws of Canada applicable therein,
        without regard to any conflict of law principles. Any legal suit,
        action, or proceeding arising out of these Terms shall be instituted
        exclusively in the courts located in the Province of Quebec, and you
        consent to the jurisdiction and venue of such courts.
      </p>

      <h3>16. Waiver and Severability</h3>
      <p>
        No waiver by SocratesLab of any breach or default shall be deemed a
        waiver of any preceding or subsequent breach or default. If any
        provision of these Terms is held invalid or unenforceable, the remaining
        provisions shall remain in full force and effect.
      </p>

      <h3>17. Entire Agreement</h3>
      <p>
        These Terms, together with the Privacy Policy and any additional terms
        to which you agree when using particular elements of the Site or
        Service, constitute the entire agreement between you and SocratesLab
        with respect to the Site and Service, and supersede all prior or
        contemporaneous communications and proposals, whether oral or written.
      </p>

      <h3>18. Termination</h3>
      <p>
        We may terminate or suspend your access to the Site or Service at any
        time, with or without cause, and with or without notice. Upon
        termination, the rights granted to you under these Terms will cease
        immediately.
      </p>

      <h3>19. Contact Information</h3>
      <p>
        If you have any questions about these Terms or the Site, please contact
        us at:
      </p>
      <p>
        SocratesLab
        <br />
        260 rue France
        <br />
        Saint-Philippe, Quebec, Canada J0L 2K0
        <br />
        info@socrateslab.ca
        <br />
        Phone: +1 (514) 792-7385
      </p>
    </div>
  );
};

export default TermsOfService;
